// [S-603] Precedent Engine (data residency) hooks — E-L2-RESIDENCY.
//
// Backed by the admin stub in apps/admin/src/routes/precedentEngine.ts:
//   POST /precedent-engine/test-connection   (live canary round-trip)
//   PUT  /precedent-engine/config            (persist mode + connector)
//
// Both return 501 not_implemented today; the UI catches that and shows an
// honest "Pending backend: S-600 / S-601" state (no fake success). The whole
// feature is gate-locked behind FEATURE_FLAG_HYBRID_ONPREM (Q4 2026).
//
// Framing (design §8a): "our engine, your environment" — the tenant supplies a
// store + endpoint; we own the embeddings/extraction. Never "your own engine".
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type PrecedentEngineBackend = "pgvector" | "firestore-remote" | "vertex-vs";
export type PrecedentEngineMode = "managed" | "resident";

// [S-614] Resident blob storage for uploaded files (cloud-agnostic).
export type PrecedentStorageProvider = "gcs" | "s3" | "azure-blob";
export type PrecedentStorageConfig = {
  provider: PrecedentStorageProvider;
  bucket: string;
  prefix?: string;
  credentialRef?: string;
};

export type PrecedentEngineConnector = {
  backend: PrecedentEngineBackend;
  endpoint: string;
  embeddingModelId: string;
  dimensions: number;
  namespace: string;
  /** [S-614] Optional resident storage for raw uploaded files. */
  storage?: PrecedentStorageConfig;
};

export type TestConnectionResult = {
  ok: boolean;
  embeddingModelId?: string;
  dimensions?: number;
};

export type PrecedentEngineConfigView = {
  mode: PrecedentEngineMode;
  connector?: PrecedentEngineConnector;
};

// The persisted config for the active studio — what the Settings form hydrates
// from so a saved "Resident" selection survives a revisit (S-609). Read-only;
// returns { mode: "managed" } when there's no active resident record.
export function usePrecedentEngineConfig(
  tenantId: string
): UseQueryResult<PrecedentEngineConfigView, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["precedent-engine-config", tenantId],
    queryFn: () => api<PrecedentEngineConfigView>("/precedent-engine/config"),
  });
}

// Live round-trip against the tenant's own store. 501 until S-600/601/602.
export function useTestPrecedentEngineConnection(): UseMutationResult<
  TestConnectionResult,
  Error,
  { tenantId: string; connector: PrecedentEngineConnector }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ tenantId, connector }) =>
      api<TestConnectionResult>("/precedent-engine/test-connection", {
        method: "POST",
        body: { tenantId, connector },
      }),
  });
}

// Persist residency mode + connector. 501 until S-600.
export function useSavePrecedentEngineConfig(): UseMutationResult<
  { ok: boolean },
  Error,
  { tenantId: string; mode: PrecedentEngineMode; connector?: PrecedentEngineConnector }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ tenantId, mode, connector }) =>
      api<{ ok: boolean }>("/precedent-engine/config", {
        method: "PUT",
        body: { tenantId, mode, connector },
      }),
  });
}
